import { Box, Stack, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { TextBlock } from 'common/ui/TextBlock';
import { JobDescriptionParsed } from 'generated-graphql/types';
import { useDrawer } from 'services/drawer/hooks';
import Yup from 'utils/validation';
import { useEffect, useState } from 'react';
import { Fields, Form } from 'common/form';
import { Refresh } from '@mui/icons-material';
import { convertHtmlToFormattedText } from 'modules/ats/utils/convertHtmlToFormattedText';
import { ToastType, useToast } from 'services/toast';
import { useConfirmationModal } from 'services/modals/hooks';

const validationSchema = Yup.object({
  description: Yup.string().required('Enter a job description')
});
export type JobDescriptionFormData = Yup.InferType<typeof validationSchema>;

const resetButtonSx: SxProps = {
  px: 0
};

const fields: Fields<JobDescriptionFormData> = {
  description: {
    type: 'textarea',
    label: 'Job description',
    subLabel: 'Used by Popp to generate requirements.',
    rows: 10
  }
};

// TODO: Add DrawerProps typing on openDrawer
type Props = {
  originalDescription: string;
  jobDescriptionText: string;
  jobDescriptionParsed: JobDescriptionParsed;
  hideEditButton?: boolean;
  onSave: (description: string) => void;
};

export const CampaignCreationJobDescriptionHtmlDrawer = ({
  originalDescription,
  jobDescriptionText,
  jobDescriptionParsed,
  hideEditButton,
  onSave
}: Props) => {
  const { showModal } = useConfirmationModal();
  const { showToast } = useToast();
  const { closeDrawer } = useDrawer();
  const [edit, setEdit] = useState(false);

  const [formData, setFormData] = useState<JobDescriptionFormData>({
    description: ''
  });

  useEffect(() => {
    const formattedDescription = convertHtmlToFormattedText(jobDescriptionText);
    setFormData((prev) => ({ ...prev, description: formattedDescription }));
  }, [jobDescriptionText]);

  return (
    <Stack width="479px" gap="20px" pb="20px">
      <TextBlock variant="drawer" title={jobDescriptionParsed.jobTitle} />
      {!edit && (
        <>
          <Typography
            sx={{
              color: 'customGrey.main',
              fontSize: '18px',
              lineHeight: '28px',
              fontWeight: 400
            }}
          >
            {jobDescriptionParsed.employerName}, {jobDescriptionParsed.location}
          </Typography>
          <b style={{ fontWeight: 600 }}>Job description</b>
          <Typography
            sx={{
              color: 'customGrey.dark',
              fontSize: '16px',
              lineHeight: '26px',
              fontWeight: 400,
              whiteSpace: 'pre-line'
            }}
          >
            {formData.description}
          </Typography>
          <Stack direction="row" gap="20px">
            <Button
              variant="primary"
              onClick={() => {
                if (formData.description !== jobDescriptionText) {
                  onSave(formData.description);
                  showToast({
                    message: 'Job description updated',
                    type: ToastType.Success
                  });
                }
                closeDrawer();
              }}
            >
              OK
            </Button>
            {!hideEditButton && (
              <Button variant="secondary" onClick={() => setEdit(true)}>
                Edit
              </Button>
            )}
          </Stack>
        </>
      )}
      {edit && (
        <Form
          defaultValues={formData}
          schema={validationSchema}
          fields={fields}
          onSubmit={(data) => {
            setFormData(data);
            setEdit(false);
          }}
          children={
            <Box>
              <Button
                variant="tertiary"
                sx={resetButtonSx}
                onClick={() => {
                  showModal({
                    title: 'Revert to original?',
                    message:
                      'You will lose any changes you or others have made to this job description.',
                    onConfirm: () => {
                      const formattedDescription =
                        convertHtmlToFormattedText(originalDescription);
                      setFormData({
                        description: formattedDescription
                      });
                      showToast({
                        message: 'Job description reset',
                        type: ToastType.Success
                      });
                      setEdit(false);
                    },
                    confirmationText: 'Reset'
                  });
                }}
              >
                <Refresh /> Reset all changes
              </Button>
            </Box>
          }
          onCancel={() => setEdit(false)}
          submitButtonProps={{
            label: 'Update'
          }}
        />
      )}
    </Stack>
  );
};
